<template>
    <div>
        <!-- List View -->
        <div v-if="isListView" data-test-view="manage-custom-attributes">
            <p>{{ $t(`usersTeams.manageCustomAttributes.listViewExplanation`) }}</p>

            <l-space :size="1" />

            <div v-show="rights.A_USER" class="title-row">
                <button
                    class="btn btn-primary btn-block btn-raised"
                    data-test-id="custom-attribute-create"
                    @click="createNewCustomAttribute()"
                >
                    {{ $t(`usersTeams.manageCustomAttributes.btnCreateNew`) }}
                </button>
            </div>

            <l-space :size="3" />

            <draggable v-model="customAttributes">
                <transition-group>
                    <div
                        v-for="(customAttribute, index) in customAttributes"
                        :key="customAttribute._id"
                        class="title"
                    >
                        <div class="row" data-test-class="customAttribute" :data-test-index="index">
                            <div
                                class="col-md-7"
                                data-test-class="customAttribute-name"
                                :data-test-id="`customAttribute-name-${index}`"
                            >
                                {{ customAttribute.name }}
                            </div>

                            <div v-show="!customAttribute.removeConfirmation" class="col-md-5">
                                <button
                                    style="margin: 0px"
                                    class="btn btn-primary btn-sm pull-right"
                                    data-test-class="customAttribute-remove"
                                    :data-test-id="`customAttribute-remove-${index}`"
                                    @click="toggleRemoveWarning(customAttribute._id, true)"
                                >
                                    <i class="fal fa-trash"></i>
                                </button>
                                <button
                                    style="margin: 0px"
                                    class="btn btn-primary btn-sm pull-right"
                                    data-test-class="customAttribute-edit"
                                    :data-test-id="`customAttribute-edit-${index}`"
                                    @click="showDetails(customAttribute)"
                                >
                                    <i class="fal fa-pencil"></i>
                                </button>
                                <button
                                    style="margin: 0px"
                                    class="btn btn-primary btn-sm pull-right"
                                >
                                    <i class="fal fa-arrows-v"></i>
                                </button>
                            </div>
                        </div>

                        <hr v-if="index !== customAttributes.length - 1" />
                    </div>
                </transition-group>
            </draggable>
            <l-space :size="2" />
        </div>
        <!--/List View -->

        <!-- Single customAttribute -->
        <div v-else data-test-view="customAttribute-upsert">
            <div>
                <span
                    class="btn-fake color-primary clickable"
                    data-test-id="back"
                    @click="goBacktoList"
                >
                    <i class="fa fa-caret-left r-default-margin-s"></i> &nbsp;
                    {{ $t(`usersTeams.manageCustomAttributes.btnBackToList`) }}
                </span>
            </div>

            <l-space :size="3" />

            <l-input-text
                ref="input-name"
                v-model="customAttribute.name"
                :label="$t(`usersTeams.manageCustomAttributes.labelCustomAttributeName`)"
                :optional="false"
                data-test-id="customAttribute-name"
                class="tw-mt-4 tw-mb-2"
            />

            <div class="tw-grid tw-grid-cols-12 tw-gap-4">
                <div class="tw-col-span-6">
                    <l-select
                        id="type"
                        v-model="customAttribute.type"
                        :label="$t(`usersTeams.manageCustomAttributes.labelCustomAttributeType`)"
                        :options="optionsType"
                        data-test-id="customAttribute-type"
                    />
                </div>
                <div class="tw-col-span-6">
                    <l-select
                        id="type"
                        v-model="customAttribute.visibility"
                        :label="
                            $t(`usersTeams.manageCustomAttributes.labelCustomAttributeVisibility`)
                        "
                        :options="optionsVisibility"
                        data-test-id="customAttribute-type"
                    />
                </div>
            </div>

            <div v-if="customAttribute.type === 'multipleChoice'" class="tw-mt-4">
                <div
                    v-for="(option, index) in customAttribute.options"
                    :key="index"
                    class="tw-grid tw-grid-cols-12"
                >
                    <div class="tw-col-span-10">
                        <l-input-text ref="option-name" v-model="option.name"> </l-input-text>
                    </div>
                    <div v-if="index" class="tw-col-span-2 tw-text-right">
                        <div
                            class="tw-mt-2 tw-ml-4 color-primary clickable"
                            @keyup.enter="removeOption(index)"
                            @click="removeOption(index)"
                        >
                            <i class="fal fa-trash"></i>
                        </div>
                    </div>
                </div>
                <div class="tw-grid tw-grid-cols-12">
                    <div class="tw-col-span-12 tw-text-right">
                        <div
                            class="tw-mt-2 color-primary clickable"
                            @click="addOption()"
                            @keyup.enter="addOption()"
                        >
                            {{ $t(`usersTeams.manageCustomAttributes.btnAddOption`) }}
                            <i class="fal fa-plus tw-ml-2"></i>
                        </div>
                    </div>
                </div>
            </div>

            <l-space :size="2" />

            <button
                class="btn btn-primary btn-block btn-raised"
                data-test-id="customAttribute-upsert"
                @click="updateCustomAttribute(customAttribute)"
            >
                <span v-if="customAttribute._id">{{
                    $t(`usersTeams.manageCustomAttributes.btnCreate`)
                }}</span>
                <span v-else>{{ $t(`usersTeams.manageCustomAttributes.btnCreate`) }}</span>
            </button>
        </div>
        <!--/Single customAttribute -->
        <l-confirmation-modal
            v-if="showRemoveWarning"
            :text="$t('usersTeams.manageCustomAttributes.removeWarning')"
            @confirm="removeCustomAttribute()"
            @cancel="toggleRemoveWarning('', false)"
        />
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import Draggable from "vuedraggable";

import LInputText from "@/components/LInputText";
import LSpace from "@/components/LSpace";
import LSelect from "@/components/LSelect";
import LConfirmationModal from "@/components/LConfirmationModal";

import teamPicture from "@/img/icons/team-icon.png";

export default {
    components: {
        Draggable,
        LInputText,
        LSpace,
        LSelect,
        LConfirmationModal,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
        close: {
            type: Function,
            required: true, // from router-view
        },
        filter: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            customAttribute: null,
            customAttributes: [],
            showRemoveWarning: false,
            removeCustomAttributeId: "",
        };
    },
    computed: {
        ...mapGetters({
            rights: "users/current/rights",
            getCustomAttributes: "users/customAttribute/list",
            getCustomAttributeDetails: "users/customAttribute/details",
            teamsSearchList: "teams/search/list",
        }),
        isListView() {
            return this.id === "list";
        },
        teams() {
            return this.teamsSearchList.map(team => ({
                _id: team._id,
                type: "team",
                name: team.name,
                desc: this.$t("typeahead.team"),
                picture: teamPicture,
            }));
        },
        optionsType() {
            return [
                {
                    value: "number",
                    key: "number",
                    text: this.$t(`usersTeams.manageCustomAttributes.typeNumber`),
                },
                {
                    value: "string",
                    key: "string",
                    text: this.$t(`usersTeams.manageCustomAttributes.typeString`),
                },
                {
                    value: "boolean",
                    key: "boolean",
                    text: this.$t(`usersTeams.manageCustomAttributes.typeBoolean`),
                },
                {
                    value: "multipleChoice",
                    key: "multipleChoice",
                    text: this.$t(`usersTeams.manageCustomAttributes.typeMultipleChoice`),
                },
            ];
        },
        optionsVisibility() {
            return [
                {
                    value: "all",
                    key: "all",
                    text: this.$t(`usersTeams.manageCustomAttributes.visibilityAll`),
                },
                {
                    value: "admin",
                    key: "admin",
                    text: this.$t(`usersTeams.manageCustomAttributes.visibilityAdmin`),
                },
            ];
        },
    },
    watch: {
        $route: {
            immediate: true,
            handler() {
                this.customAttribute = this.getCustomAttributeDetails(this.id);
            },
        },
        customAttributes: {
            handler(newValue) {
                // If all customAttributes are already in order, break of the watcher loop
                if (newValue.every((customAttribute, order) => customAttribute.order === order)) {
                    return;
                }

                // Optimistic update of customAttributes to have a snappy UI
                this.customAttributes = newValue.map((customAttribute, order) => ({
                    ...customAttribute,
                    order,
                }));

                // Dispatch
                this.$store.dispatch({
                    type: "users/customAttribute/updateOrder",
                    payload: { customAttributes: this.customAttributes },
                });
            },
        },
    },
    created() {
        this.customAttributes = this.getCustomAttributes;
    },
    methods: {
        createNewCustomAttribute() {
            this.$router.push(`/team/members/customAttributes/new`);
        },
        showDetails(customAttribute) {
            this.$router.push(`/team/members/customAttributes/${customAttribute._id}`);
        },
        isValid() {
            const inputs = Object.keys(this.$refs)
                .map(key => (key.startsWith("input-") ? this.$refs[key] : null))
                .filter(Boolean);

            return inputs.reduce((a, c) => c.validate() && a, true);
        },
        updateCustomAttribute(customAttribute) {
            if (!this.isValid()) {
                return;
            }

            this.$store
                .dispatch({
                    type: "users/customAttribute/update",
                    payload: {
                        customAttribute: {
                            ...customAttribute,
                            order: customAttribute.order ?? this.customAttribute.length,
                        },
                    },
                })
                .then(() => {
                    this.customAttributes = this.getCustomAttributes;
                    this.goBacktoList();
                });
        },
        toggleRemoveWarning(customAttributeId, show) {
            this.showRemoveWarning = show;
            this.removeCustomAttributeId = customAttributeId;
        },
        removeCustomAttribute() {
            let customAttribute = this.customAttributes.find(
                c => c._id === this.removeCustomAttributeId,
            );
            this.$store
                .dispatch({
                    type: "users/customAttribute/update",
                    payload: {
                        customAttribute: {
                            ...customAttribute,
                            active: false,
                        },
                    },
                })
                .then(() => {
                    this.customAttributes = this.getCustomAttributes;
                    this.showRemoveWarning = false;
                });
        },

        goBacktoList() {
            this.$router.push(`/team/members/customAttributes/list`);
        },
        addOption() {
            this.customAttribute.options.push({ name: "" });
        },
        removeOption(index) {
            this.customAttribute.options.splice(index, 1);
        },
    },
};
</script>

<style scoped></style>
